import type { TOC } from '@ember/component/template-only';
import CardGrid from 'tio-reimagined/components/card-grid';
import type { CardGridDensity } from 'tio-reimagined/components/card-grid';
import SectionHeader from 'tio-reimagined/components/section-header';
import SmartActionCard from './smart-action-card';
import type { SmartAction } from './smart-action-card';
import { PhLightbulb } from 'ember-phosphor-icons';

export interface SmartActionsSectionSignature {
  Args: {
    /** Array of SmartAction items to render */
    actions: SmartAction[];
    /** Section title (default: "Recommended Actions") */
    title?: string;
    /** Section subtitle */
    subtitle?: string;
    /**
     * Grid density preset — passed through to CardGrid.
     *   - 'narrow'  — min column width 200px (4-up at hero width)
     *   - 'medium'  — min column width 260px (3-up at typical content width, default)
     *   - 'wide'    — min column width 360px (2-up at typical content width)
     *
     * The grid reflows based on its OWN width (container query), not the
     * viewport, so a SmartActionsSection rendered in a narrow sidebar
     * collapses to one column automatically without per-page logic.
     */
    density?: CardGridDensity;
  };
  Element: HTMLElement;
}

/**
 * SmartActionsSection — encapsulates the repeated pattern of
 * SectionHeader + responsive grid + SmartActionCard loop.
 *
 * Used on documents, college-planning, idr, syf, wellness, and other pages.
 */
const SmartActionsSection: TOC<SmartActionsSectionSignature> = <template>
  <section ...attributes>
    <SectionHeader
      @icon={{PhLightbulb}}
      @title={{if @title @title "Recommended Actions"}}
      @subtitle={{@subtitle}}
      @iconClass="text-amber-500"
    />
    <CardGrid @density={{if @density @density "medium"}}>
      {{#each @actions as |smartAction|}}
        <SmartActionCard @item={{smartAction}} />
      {{/each}}
    </CardGrid>
  </section>
</template>;

export default SmartActionsSection;
export { type SmartAction };
